/**
 * Price Book Sharing section — CR-6346 (reworked after QA).
 *
 * Client Admin Setup > Purchasing control for whether suppliers can view price
 * books (contract prices) via the supplier portal. Three pieces:
 *   1. A three-state flag, all of them selectable: All Off (default), All On,
 *      and Custom (Selected Suppliers). The first pass shipped Custom as a
 *      read-only, system-generated state and QA rejected it — Custom is now
 *      chosen by the admin, with the per-supplier grants held server-side on
 *      SupplierCustomerRelationship.price_book_shared.
 *   2. A multi-select of which price book types are shared. Options come from
 *      the client's configured contract types, not a hardcoded list. Leaving it
 *      empty shares every type — the copy below says so, because a silent
 *      "empty means nothing" would read as the feature being broken.
 *   3. An expandable change history, since QA also found no audit trail or
 *      timestamp for the configuration.
 *
 * Changes are persisted through the platform settings endpoint (per-tenant
 * overlay) and recorded in the audit trail server-side.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, BookOpen, Info } from 'lucide-react';
import { platformSettingsAPI, purchasingAPI, fetchAllPages } from '../../../lib/api';
import type { ContractType } from '../../../lib/api';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { PriceBookSupplierPicker } from './PriceBookSupplierPicker';
import { PriceBookSharingHistory } from './PriceBookSharingHistory';

type SharingFlag = 'all_off' | 'all_on' | 'custom';

const FLAG_OPTIONS: { value: SharingFlag; label: string }[] = [
  { value: 'all_off', label: 'All Off' },
  { value: 'all_on', label: 'All On' },
  { value: 'custom', label: 'Custom (Selected Suppliers)' },
];

const FLAG_HINTS: Record<SharingFlag, string> = {
  all_off: 'No supplier can view price books. This is the default.',
  all_on: 'Every supplier can view the shared price books for this company.',
  custom: 'Only the suppliers you select below can view the shared price books.',
};

export function PriceBookSharingSection() {
  const [loading, setLoading] = useState(true);
  const [flag, setFlag] = useState<SharingFlag>('all_off');
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  // Bumped after any save so the history panel and picker re-read.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const [settingsRes, allTypes] = await Promise.all([
          platformSettingsAPI.getSettings(),
          fetchAllPages<ContractType>((page) => purchasingAPI.getContractTypes({ enabled: true, page_size: 100, page })),
        ]);
        const settings = settingsRes.data;
        setFlag((settings.price_book_sharing_flag ?? 'all_off') as SharingFlag);
        setSelectedTypeIds(
          Array.isArray(settings.shared_price_book_types) ? settings.shared_price_book_types : [],
        );
        setContractTypes(allTypes);
      } catch (err: unknown) {
        console.error('Error loading price book sharing settings:', err);
        toast.error('Failed to load Price Book Sharing settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectorDisabled = flag === 'all_off';

  const handleFlagChange = async (next: SharingFlag) => {
    if (next === flag) return;
    const previous = flag;
    setFlag(next);
    try {
      await platformSettingsAPI.patchSettings({ price_book_sharing_flag: next });
      const label = FLAG_OPTIONS.find((o) => o.value === next)?.label ?? next;
      toast.success(`Price book sharing set to ${label}`);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      console.error('Error saving price book sharing flag:', err);
      toast.error('Failed to update price book sharing');
      setFlag(previous);
    }
  };

  const handleTypeToggle = async (id: number, checked: boolean) => {
    const previous = selectedTypeIds;
    const next = checked
      ? [...previous, id]
      : previous.filter((t) => t !== id);
    setSelectedTypeIds(next);
    try {
      await platformSettingsAPI.patchSettings({ shared_price_book_types: next });
      toast.success('Shared price book types updated');
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      console.error('Error saving shared price book types:', err);
      toast.error('Failed to update shared price book types');
      setSelectedTypeIds(previous);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Price Book Sharing
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Control whether your suppliers can view price books via the supplier portal, and choose
          which price book types are shared. Suppliers see a read-only view of their own contracts
          — never cost, PPV or negotiation detail.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global three-state flag */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Sharing</Label>
          <div className="inline-flex flex-wrap overflow-hidden rounded-md border border-input">
            {FLAG_OPTIONS.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleFlagChange(option.value)}
                aria-pressed={flag === option.value}
                className={cn(
                  'px-4 py-2 text-sm font-medium transition-colors',
                  index > 0 && 'border-l border-input',
                  flag === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{FLAG_HINTS[flag]}</p>
        </div>

        {/* Supplier picker — only meaningful in the Custom state */}
        {flag === 'custom' && (
          <PriceBookSupplierPicker refreshKey={refreshKey} />
        )}

        {/* Price book type multi-select */}
        <div className="space-y-2">
          <Label className={cn('text-sm font-medium', selectorDisabled && 'text-muted-foreground')}>
            Shared price book types
          </Label>
          <p className="text-xs text-muted-foreground">
            {selectorDisabled
              ? 'Enable sharing to choose which price book types are shared.'
              : 'Select which price book types suppliers can view. Applies to all suppliers with access.'}
          </p>
          {!selectorDisabled && selectedTypeIds.length === 0 && contractTypes.length > 0 && (
            <p className="flex items-start gap-1.5 rounded-md border border-input bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                No types selected, so <span className="font-medium">all price book types are
                shared</span>. Select specific types to narrow what suppliers can see.
              </span>
            </p>
          )}
          {contractTypes.length === 0 ? (
            <p className="rounded-md border border-dashed border-input px-4 py-3 text-sm text-muted-foreground">
              No price book types configured. Add contract types in the section above to choose
              which are shared.
            </p>
          ) : (
            <div
              className={cn(
                'grid grid-cols-1 gap-2 sm:grid-cols-2',
                selectorDisabled && 'opacity-50',
              )}
            >
              {contractTypes.map((ct) => (
                <label
                  key={ct.id}
                  htmlFor={`pb-type-${ct.id}`}
                  className={cn(
                    'flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3',
                    selectorDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-muted',
                  )}
                >
                  <Checkbox
                    id={`pb-type-${ct.id}`}
                    checked={selectedTypeIds.includes(ct.id)}
                    disabled={selectorDisabled}
                    onCheckedChange={(checked) => handleTypeToggle(ct.id, checked === true)}
                  />
                  <span className="rounded bg-muted px-2 py-0.5 font-mono text-sm">
                    {ct.abbreviation}
                  </span>
                  <span className="text-sm">{ct.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Audit trail */}
        <PriceBookSharingHistory refreshKey={refreshKey} />
      </CardContent>
    </Card>
  );
}
